import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { ShoppingCart, Search, Barcode, Maximize, Minimize, Ban } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useCart } from "@/hooks/useCart";
import { useOrders } from "@/hooks/useOrders";
import { useCurrentOrderItems } from "@/hooks/useCurrentOrderItems";
import { useInternalCombos } from "@/hooks/useInternalCombos";
import type { InternalComboWithDetails } from "@/hooks/useCombos";
import { useCashRegister } from "@/hooks/useCashRegister";
import { printConferenceBill } from "@/lib/order-printing";
import { 
  getCancellationPrintSettings, 
  printBatchCancellationReceipt,
  type CancellationData,
} from "@/lib/cancellation-printing";
import { useRestaurant } from "@/hooks/useRestaurant";
import { useStockAvailability } from "@/hooks/useStockAvailability";
import { useStockSettings } from "@/hooks/useStockSettings";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useSQLiteSync } from "@/hooks/useSQLiteSync";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { useTables } from "@/hooks/useTables";
import { useCommands } from "@/hooks/useCommands";
import { useAuth } from "@/hooks/useAuth";
import { useServiceTax } from "@/hooks/useServiceTax";
import { useCommandEntryValidation } from "@/hooks/useCommandEntryValidation";
import { supabase } from "@/integrations/supabase/client";
import { CategoryTabs } from "@/components/pdv/CategoryTabs";
import { ProductGrid } from "@/components/pdv/ProductGrid";
import { CheckoutDialog, PaymentEntry } from "@/components/pdv/CheckoutDialog";
import { CheckoutScreen } from "@/components/pdv/CheckoutScreen";
import { OfflineStatusIndicator } from "@/components/pdv/OfflineStatusIndicator";
import { SQLiteStatusIndicator } from "@/components/pdv/SQLiteStatusIndicator";
import { CacheExpirationAlert } from "@/components/pdv/CacheExpirationAlert";
import { ItemNotesDialog } from "@/components/pdv/ItemNotesDialog";
import { PDVSelectionScreen, SelectedDestination } from "@/components/pdv/PDVSelectionScreen";
import { PDVOperatorLogin } from "@/components/pdv/PDVOperatorLogin";
import { usePDVOperators } from "@/hooks/usePDVOperators";
import { usePDVOperatorSettings } from "@/hooks/usePDVOperatorSettings";
import { PDVOrderHeader } from "@/components/pdv/PDVOrderHeader";
import { PDVActionBar } from "@/components/pdv/PDVActionBar";
import { PDVItemsList } from "@/components/pdv/PDVItemsList";
import { WaiterSessionsDialog } from "@/components/pdv/WaiterSessionsDialog";
import { ComboDetailSheet } from "@/components/combos/ComboDetailSheet";
import { PeriodClosedOverlay } from "@/components/pdv/PeriodClosedOverlay";
import { PeriodCloseRequiredOverlay } from "@/components/pdv/PeriodCloseRequiredOverlay";
import { CashRegisterClosedOverlay } from "@/components/pdv/CashRegisterClosedOverlay";
import { NfceEmissionDialog } from "@/components/fiscal/NfceEmissionDialog";
import { CustomerIdentificationDialog } from "@/components/fiscal/CustomerIdentificationDialog";
import { GroupTicketsDialog } from "@/components/pdv/GroupTicketsDialog";
import { TransferItemsDialog } from "@/components/pdv/TransferItemsDialog";
import { CancelOrderDialog } from "@/components/pdv/CancelOrderDialog";
import { OrderDetailsPanel } from "@/components/pdv/OrderDetailsPanel";
import { CustomerDataDialog } from "@/components/pdv/CustomerDataDialog";
import { NicknameDialog } from "@/components/pdv/NicknameDialog";
import { useFiscalConfig } from "@/hooks/useFiscalConfig";
import { useNfceEmission } from "@/hooks/useNfceEmission";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type { Tables } from "@/integrations/supabase/types";
import type { ComboSelection } from "@/hooks/useCart";
import type { PaymentMethod } from "@/hooks/usePaymentMethods";

type Product = Tables<"products"> & { barcode?: string | null };

/**
 * Retorna o código fiscal SEFAZ para emissão de NFC-e
 * Usa fiscal_code configurado ou deduz do tipo/nome do método
 */
function getFiscalPaymentCode(pm: PaymentMethod): string {
  // Se tem fiscal_code configurado, usar direto
  if (pm.fiscal_code) return pm.fiscal_code;
  
  // Fallback baseado no tipo
  if (pm.type === 'cash') return '01'; // Dinheiro
  if (pm.type === 'digital_wallet') return '17'; // PIX
  
  // Para cartão, verificar pelo nome (fallback)
  const nameLower = pm.name.toLowerCase();
  if (nameLower.includes('débito') || nameLower.includes('debito')) return '04';
  if (nameLower.includes('crédito') || nameLower.includes('credito')) return '03';
  if (nameLower.includes('vale') && nameLower.includes('alimenta')) return '10';
  if (nameLower.includes('vale') && nameLower.includes('refeição')) return '11';
  
  // Default para cartão de crédito
  return '03';
}

export default function PDVPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userName, setUserName] = useState<string | null>(null);
  const { products, fetchProducts, isLoading: productsLoading } = useProducts();
  const { categories, fetchCategories } = useCategories();
  const { tables, fetchTables, updateTable } = useTables();
  const { commands, fetchCommands, updateCommand } = useCommands();
  const { isOpen: isCashRegisterOpen, isPeriodOpen, isLoading: cashRegisterLoading, checkRequireManualClose } = useCashRegister();
  const {
    items,
    comboItems,
    destination,
    selectDestination,
    orderInfo,
    setOrderInfo,
    addItem,
    removeItem,
    updateQuantity,
    updateItemNotes,
    addCombo,
    removeCombo,
    updateComboQuantity,
    clearCart,
    clearItems,
    subtotal,
    itemCount,
  } = useCart();
  const { createOrder, sendItemsToOrder, closeOrder } = useOrders();
  const { activeRestaurant } = useRestaurant();
  const { calculateServiceTax, serviceTaxPercentage } = useServiceTax();
  const { getEntryForCommand } = useCommandEntryValidation();
  const { requireOperatorLogin } = usePDVOperatorSettings();
  const { operators: pdvOperators } = usePDVOperators(activeRestaurant?.id);
  const [operatorAuthenticated, setOperatorAuthenticated] = useState(false);
  
  // Fetch user name from profile
  useEffect(() => {
    const fetchUserName = async () => {
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();
        setUserName(data?.full_name || user.email?.split("@")[0] || null);
      }
    };
    fetchUserName();
  }, [user]);

  // Update orderInfo with waiterName when destination changes
  useEffect(() => {
    if (destination && userName) {
      setOrderInfo((prev) => ({
        ...prev,
        waiterName: userName,
      }));
    }
  }, [destination, userName, setOrderInfo]);
  
  // Fetch existing order items for the selected destination
  const {
    sentItems,
    cancelledItems,
    isLoading: isLoadingExistingOrder,
    orderId: existingOrderId,
    orderNumber: existingOrderNumber,
    refetch: refetchSentItems,
    cancelSentItem,
    isCancelling,
    decreaseItemQuantity,
    fetchError: orderFetchError,
  } = useCurrentOrderItems(
    destination?.type === "table" ? destination.id : undefined,
    destination?.type === "tab" ? destination.id : undefined
  );

  // Seed nickname (customer_name) from existing order when it loads
  useEffect(() => {
    if (!existingOrderId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("orders")
        .select("customer_name")
        .eq("id", existingOrderId)
        .maybeSingle();
      if (cancelled) return;
      if (data?.customer_name) {
        setOrderInfo((prev) =>
          prev.customerName === data.customer_name ? prev : { ...prev, customerName: data.customer_name }
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existingOrderId, setOrderInfo]);

  // Save nickname: update local orderInfo + persist to orders/commands if order exists
  const handleSetNickname = useCallback(async (nickname: string) => {
    const normalized = nickname.trim();
    setOrderInfo((prev) => ({ ...prev, customerName: normalized || undefined }));

    if (existingOrderId) {
      try {
        const { error } = await supabase
          .from("orders")
          .update({ customer_name: normalized || null })
          .eq("id", existingOrderId);
        if (error) throw error;

        if (destination?.type === "tab" && destination.id) {
          await updateCommand(destination.id, { customer_name: normalized || null });
        }
        toast.success(normalized ? "Apelido atualizado" : "Apelido removido");
      } catch (err) {
        console.error("[Nickname] update failed", err);
        toast.error("Erro ao salvar apelido");
      }
    } else {
      toast.success(normalized ? "Apelido definido" : "Apelido removido");
    }
  }, [existingOrderId, destination, setOrderInfo, updateCommand]);

  const destinationIsOccupied = useMemo(() => {
    if (!destination) return false;
    if (destination.type === "table") {
      const status = tables.find(t => t.id === destination.id)?.status;
      return status === "occupied" || status === "grouped";
    }
    if (destination.type === "tab") {
      const status = commands.find(c => c.id === destination.id)?.status;
      return status === "in_use" || status === "grouped";
    }
    return false;
  }, [destination, tables, commands]);

  // Safety: if destination is occupied but we still couldn't resolve an active order, block checkout & prompt reload.
  const isConsumptionUnresolved = useMemo(() => {
    return Boolean(
      destinationIsOccupied &&
      !existingOrderId &&
      sentItems.length === 0 &&
      !isLoadingExistingOrder
    );
  }, [destinationIsOccupied, existingOrderId, sentItems.length, isLoadingExistingOrder]);

  // Pending cancellations state (not yet saved to database)
  const [pendingCancellations, setPendingCancellations] = useState<{ itemId: string; reason?: string }[]>([]);

  // Mark item for cancellation (local state only)
  const markForCancellation = useCallback((itemId: string, reason?: string) => {
    setPendingCancellations(prev => [...prev, { itemId, reason }]);
  }, []);

  // Undo pending cancellation
  const undoCancellation = useCallback((itemId: string) => {
    setPendingCancellations(prev => prev.filter(c => c.itemId !== itemId));
  }, []);

  // Calculate sent items total (excluding pending cancellations)
  const activeSentItems = useMemo(() => {
    return sentItems.filter(item => !pendingCancellations.some(c => c.itemId === item.id));
  }, [sentItems, pendingCancellations]);

  const sentItemsTotal = useMemo(() => {
    return activeSentItems.reduce((sum, item) => sum + Number(item.total_price), 0);
  }, [activeSentItems]);

  // Combined subtotal (active sent items + new items)
  const combinedSubtotal = subtotal + sentItemsTotal;
  const combinedItemCount = itemCount + activeSentItems.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate service tax for display
  const serviceTaxAmount = calculateServiceTax(combinedSubtotal);

  // Get entry info for commands
  const commandEntry = useMemo(() => {
    if (destination?.type === "tab") {
      const command = commands.find(c => c.id === destination.id);
      if (command) {
        return getEntryForCommand(command.number);
      }
    }
    return null;
  }, [destination, commands, getEntryForCommand]);

  const entryAmount = commandEntry?.amount ?? null;
  const entryTypeName = commandEntry?.entry_type_name;

  // Calculate display total
  const displayTotal = combinedSubtotal + serviceTaxAmount + (entryAmount ?? 0);
  const { 
    productStockMap, 
    loadAllProductsAvailability, 
    checkProductAvailability 
  } = useStockAvailability();
  const { stockBlockSales } = useStockSettings();
  const { isOnline, isSyncing, pendingCount, triggerSync, updatePendingCount } = useOfflineSync();
  const { isOnline: isSQLiteOnline } = useSQLiteSync();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [itemQuantity, setItemQuantity] = useState(1);
  
  // Manual close required state
  const [manualCloseStatus, setManualCloseStatus] = useState<{
    shouldBlock: boolean;
    hasCashRegisterOpen: boolean;
    periodDate: string | null;
    hoursSinceLastActivity: number;
    hasOpenConsumptions: boolean;
    openConsumptionsCount: number;
    canTransferPending: boolean;
  } | null>(null);

  // Derived: partial block = block new items but allow payments on open consumptions
  const isPartialBlock = manualCloseStatus?.shouldBlock && manualCloseStatus.hasOpenConsumptions;
  const isFullBlock = manualCloseStatus?.shouldBlock && !manualCloseStatus.hasOpenConsumptions;
  const hasCheckedManualClose = useRef(false);
  
  // Combo state
  const [selectedCombo, setSelectedCombo] = useState<InternalComboWithDetails | null>(null);
  const [comboSheetOpen, setComboSheetOpen] = useState(false);
  const [groupTicketsOpen, setGroupTicketsOpen] = useState(false);
  const [transferItemsOpen, setTransferItemsOpen] = useState(false);
  const [cancelOrderOpen, setCancelOrderOpen] = useState(false);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [customerDataOpen, setCustomerDataOpen] = useState(false);
  const [nicknameOpen, setNicknameOpen] = useState(false);
  // Pending customer identification (when set before the first order exists)
  const [pendingCustomer, setPendingCustomer] = useState<{
    customerId: string | null;
    customerCpf: string | null;
    customerName?: string | null;
  } | null>(null);
  
  // Fetch combos for PDV
  const { data: combos = [] } = useInternalCombos('pdv');
  
  // NFC-e emission state (at page level so modals persist when checkout closes)
  const { config: fiscalConfig } = useFiscalConfig();
  const { emitNfce, isEmitting: isEmittingNfce, lastResult: nfceResult } = useNfceEmission();
  const [nfceDialogOpen, setNfceDialogOpen] = useState(false);
  const [customerIdDialogOpen, setCustomerIdDialogOpen] = useState(false);
  const [pendingNfceOrderId, setPendingNfceOrderId] = useState<string | null>(null);
  const [pendingNfcePayments, setPendingNfcePayments] = useState<PaymentEntry[]>([]);
  
  const [quickNotesDialog, setQuickNotesDialog] = useState<{
    open: boolean;
    productId: string;
    productName: string;
    currentNotes: string;
  }>({
    open: false,
    productId: "",
    productName: "",
    currentNotes: "",
  });

  // Show toast with observation option
  const showAddedToast = useCallback((product: Product) => {
    toast.success(`${product.name} adicionado`, {
      duration: 3000,
    });
  }, []);

  const handleQuickNotesSave = useCallback((notes: string) => {
    if (quickNotesDialog.productId) {
      updateItemNotes(quickNotesDialog.productId, notes);
    }
  }, [quickNotesDialog.productId, updateItemNotes]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error("Fullscreen error:", err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error("Exit fullscreen error:", err);
      });
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Auto-enter fullscreen when PDV page loads
  useEffect(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log("Could not auto-enter fullscreen:", err);
      });
    }

    // Check manual close requirement once on page load
    if (!hasCheckedManualClose.current && checkRequireManualClose) {
      hasCheckedManualClose.current = true;
      // Always force check — even if requireManualClose setting is off,
      // we need to block when auto-close failed due to open registers
      checkRequireManualClose(true).then((result) => {
        if (result.shouldBlock) {
          setManualCloseStatus(result);
        }
      });
    }
  }, []);

  // Exit fullscreen and navigate back to dashboard
  const handleExitPDV = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
    navigate("/dashboard");
  }, [navigate]);

  // Find product by barcode
  const findProductByBarcode = useCallback((barcode: string): Product | null => {
    const product = (products as Product[]).find(
      (p) => p.barcode && p.barcode.toLowerCase() === barcode.toLowerCase() && p.is_active
    );
    return product || null;
  }, [products]);

  // Handle barcode scan
  const handleBarcodeScanned = useCallback(async (barcode: string) => {
    if (!destination) return; // Only scan when in order mode

    const product = findProductByBarcode(barcode);
    
    if (product) {
      const currentAvailability = productStockMap.get(product.id);
      if (stockBlockSales && currentAvailability && !currentAvailability.available) {
        toast.error(`Produto bloqueado: ${product.name}`, {
          description: "Estoque insuficiente para venda.",
        });
        return;
      }

      const currentCartItem = items.find(item => item.product.id === product.id);
      const newQuantity = (currentCartItem?.quantity || 0) + 1;
      const availability = await checkProductAvailability(product.id, newQuantity);

      if (!availability.available && stockBlockSales) {
        toast.error(`Quantidade máxima atingida: ${product.name}`, {
          description: "Não há estoque suficiente para mais unidades.",
        });
        return;
      }

      addItem(product);
      showAddedToast(product);
    } else {
      toast.error("Produto não encontrado", {
        description: `Código de barras: ${barcode}`,
      });
    }
  }, [destination, findProductByBarcode, productStockMap, stockBlockSales, items, checkProductAvailability, addItem, showAddedToast]);

  // Barcode scanner hook - only active when destination is selected
  const { isScanning } = useBarcodeScanner(handleBarcodeScanned, !!destination);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isTyping = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
    
    // F11 = Fullscreen (always available)
    if (e.key === "F11") {
      e.preventDefault();
      toggleFullscreen();
      return;
    }

    // Only when destination is selected (PDV products view)
    if (!destination) return;

    // Ctrl+Enter or Cmd+Enter = Send items
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if ((itemCount > 0 || pendingCancellations.length > 0) && !isSending) {
        handleSendItems();
      }
      return;
    }

    // F12 = Open checkout (if there are items)
    if (e.key === 'F12') {
      e.preventDefault();
      if (combinedItemCount > 0 && !checkoutOpen) {
        setCheckoutOpen(true);
      }
      return;
    }

    // Escape = Back to selection or clear search
    if (e.key === "Escape") {
      if (checkoutOpen) {
        return; // Let checkout handle its own escape
      }
      if (searchQuery) {
        setSearchQuery("");
        searchInputRef.current?.focus();
      } else if (selectedCategory) {
        setSelectedCategory(null);
      } else {
        handleBackToSelection();
      }
      return;
    }

    // "/" = Focus search
    if (e.key === "/" && !isTyping) {
      e.preventDefault();
      searchInputRef.current?.focus();
      return;
    }
  }, [searchQuery, itemCount, checkoutOpen, selectedCategory, toggleFullscreen, destination, isSending, combinedItemCount]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchTables();
    fetchCommands();
  }, [fetchProducts, fetchCategories, fetchTables, fetchCommands]);

  // Refresh tables and commands when returning to selection screen
  useEffect(() => {
    if (!destination) {
      fetchTables();
      fetchCommands();
    }
  }, [destination, fetchTables, fetchCommands]);

  // Load stock availability when products change
  useEffect(() => {
    if (products.length > 0) {
      const productIds = products.map(p => p.id);
      loadAllProductsAvailability(productIds, products);
    }
  }, [products, loadAllProductsAvailability]);

  // Filter active products only
  const activeProducts = useMemo(() => {
    return products.filter((p) => p.is_active);
  }, [products]);

  // Filter by category and search
  const filteredProducts = useMemo(() => {
    return (activeProducts as Product[]).filter((product) => {
      const matchesCategory =
        selectedCategory === null || product.category_id === selectedCategory;
      
      const lowerSearch = searchQuery.toLowerCase();
      const matchesSearch =
        searchQuery === "" ||
        product.name.toLowerCase().includes(lowerSearch) ||
        (product.barcode && product.barcode.toLowerCase().includes(lowerSearch));
      
      return matchesCategory && matchesSearch;
    });
  }, [activeProducts, selectedCategory, searchQuery]);

  // Product count by category
  const productCountByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    (activeProducts as Product[]).forEach((product) => {
      if (product.category_id) {
        counts.set(product.category_id, (counts.get(product.category_id) || 0) + 1);
      }
    });
    return counts;
  }, [activeProducts]);

  // Only active categories with products
  const activeCategories = useMemo(() => {
    const categoriesWithProducts = new Set(
      activeProducts.map((p) => p.category_id).filter(Boolean)
    );
    return categories.filter(
      (c) => c.is_active && categoriesWithProducts.has(c.id)
    );
  }, [categories, activeProducts]);

  // Get color from selected category for product borders
  const selectedCategoryColor = useMemo(() => {
    if (!selectedCategory) return null;
    const category = activeCategories.find(c => c.id === selectedCategory);
    return category?.color ?? null;
  }, [selectedCategory, activeCategories]);

  const handleProductClick = async (product: Product) => {
    // Block new item additions when period is in partial block mode
    if (isPartialBlock) {
      toast.error("Lançamentos bloqueados", {
        description: "O período precisa ser fechado. Apenas recebimentos são permitidos.",
      });
      return;
    }

    const currentAvailability = productStockMap.get(product.id);
    if (stockBlockSales && currentAvailability && !currentAvailability.available) {
      toast.error("Venda bloqueada - Estoque insuficiente", {
        description: "Este produto não pode ser vendido pois não há ingredientes suficientes.",
      });
      return;
    }

    const currentCartItem = items.find(item => item.product.id === product.id);
    const newQuantity = (currentCartItem?.quantity || 0) + itemQuantity;
    const availability = await checkProductAvailability(product.id, newQuantity);

    if (!availability.available) {
      const ingredientNames = availability.insufficientIngredients
        .map(ing => `${ing.ingredientName} (${ing.available.toFixed(1)}/${ing.required.toFixed(1)} ${ing.unit})`)
        .join(", ");
      
      if (stockBlockSales) {
        toast.error(`Quantidade máxima atingida: ${ingredientNames}`, {
          description: "Não há estoque suficiente para mais unidades.",
        });
        return;
      }
      
      toast.warning(`Estoque insuficiente: ${ingredientNames}`, {
        description: "O produto foi adicionado, mas verifique o estoque.",
        duration: 4000,
      });
    }

    addItem(product, itemQuantity);
    showAddedToast(product);
    setItemQuantity(1); // Reset quantity after adding
  };

  const handleComboClick = (combo: InternalComboWithDetails) => {
    if (isPartialBlock) {
      toast.error("Lançamentos bloqueados", {
        description: "O período precisa ser fechado. Apenas recebimentos são permitidos.",
      });
      return;
    }
    setSelectedCombo(combo);
    setComboSheetOpen(true);
  };

  const handleAddComboToCart = (
    combo: InternalComboWithDetails,
    selection: ComboSelection,
    calculatedPrice: number,
    quantity: number,
    notes?: string
  ) => {
    addCombo(combo, selection, calculatedPrice, quantity, notes);
    toast.success(`${combo.name} adicionado`);
  };

  const handleCheckout = () => {
    if (isConsumptionUnresolved) {
      toast.error("Consumo ainda não carregou", {
        description: "A mesa/comanda está marcada como ocupada. Recarregando o consumo...",
      });
      void refetchSentItems();
      return;
    }
    setCheckoutOpen(true);
  };

  const handleConfirmOrder = async (
    discount: number,
    payments: PaymentEntry[],
    serviceTax: number,
    couvertQuantity: number,
    couvertAmount: number,
    tableId?: string,
    commandId?: string,
    entryDifference?: number,
    entryId?: string,
    peopleCount?: number,
    onOrderCreated?: (orderId: string) => void,
    subtotalParam?: number
  ) => {
    setIsSubmitting(true);
    let order = null;
    
    const resolvedTableId = tableId || (destination?.type === "table" ? destination.id : undefined);
    const resolvedCommandId = commandId || (destination?.type === "tab" ? destination.id : undefined);

    if (items.length > 0 || comboItems.length > 0) {
      // 1. Process pending cancellations first
      for (const cancellation of pendingCancellations) {
        await cancelSentItem(cancellation.itemId, cancellation.reason);
      }

      // 2. Check if we need to add to existing order
      if (sentItems.length > 0 && existingOrderId) {
        // Send new items to existing order, then close it
        // If offline, skip sendItemsToOrder (would fail) and go straight to closeOrder
        if (!isSQLiteOnline) {
          console.log("[PDVPage] Offline: skipping sendItemsToOrder, going directly to closeOrder");
        } else {
          await sendItemsToOrder(items, orderInfo, resolvedTableId, resolvedCommandId, orderInfo.peopleCount, comboItems);
        }
        order = await closeOrder(existingOrderId, discount, payments, serviceTax, couvertQuantity, couvertAmount, resolvedTableId, resolvedCommandId, entryDifference, entryId, peopleCount, subtotalParam);
      } else {
        // No sent items, create new order directly
        order = await createOrder(items, orderInfo, discount, payments, serviceTax, couvertQuantity, couvertAmount, resolvedTableId, resolvedCommandId, entryDifference, entryId, peopleCount);
      }
    } else if (sentItems.length > 0 && existingOrderId) {
      // Only sent items exist, close the existing order
      order = await closeOrder(existingOrderId, discount, payments, serviceTax, couvertQuantity, couvertAmount, resolvedTableId, resolvedCommandId, entryDifference, entryId, peopleCount, subtotalParam);
    } else if (existingOrderId) {
      // No items left (all cancelled), still close the order to release table/command
      order = await closeOrder(existingOrderId, discount, payments, serviceTax, couvertQuantity, couvertAmount, resolvedTableId, resolvedCommandId, entryDifference, entryId, peopleCount, subtotalParam);
    }
    
    setIsSubmitting(false);

    if (order) {
      // Notify caller about the created order before clearing the state
      onOrderCreated?.(order.id);

      setCheckoutOpen(false);
      if (destination?.type === "counter") {
        // Balcão: stay on counter screen ready for next sale
        clearItems();
        setOrderInfo({ ...orderInfo, customerName: undefined, notes: undefined });
      } else {
        clearCart();
      }
      setPendingCancellations([]); // Clear pending cancellations after successful checkout
      updatePendingCount();
    }
  };

  // Get destination label for checkout screen
  const getDestinationLabel = () => {
    if (!destination) return "";
    if (destination.type === "table") {
      const table = tables.find(t => t.id === destination.id);
      return `Mesa ${destination.number}${table?.name ? ` - ${table.name}` : ""}`;
    }
    return `Comanda ${destination.number}`;
  };

  // NFC-e emission handler - called from CheckoutScreen after order is created
  const handleNfceEmission = useCallback(async (orderId: string, payments: PaymentEntry[]) => {
    setPendingNfceOrderId(orderId);
    setPendingNfcePayments(payments);

    // Check if order already has customer_cpf linked (from CustomerDataDialog)
    const { data: orderData } = await supabase
      .from("orders")
      .select("customer_cpf")
      .eq("id", orderId)
      .single();

    if (orderData?.customer_cpf) {
      // Skip identification dialog, emit directly with existing CPF
      setNfceDialogOpen(true);
      await emitNfce(
        orderId,
        payments.map(p => ({
          method: getFiscalPaymentCode(p.paymentMethod),
          amount: Math.round(p.amount * 100),
        })),
        { cpf: orderData.customer_cpf }
      );
      setPendingNfceOrderId(null);
      setPendingNfcePayments([]);
    } else {
      setCustomerIdDialogOpen(true);
    }
  }, [emitNfce]);

  // Handle customer identification and emit NFC-e
  const handleCustomerIdentificationConfirm = useCallback(async (customerData: { cpf?: string; nome?: string } | null) => {
    if (!pendingNfceOrderId) return;
    
    setCustomerIdDialogOpen(false);
    setNfceDialogOpen(true);
    
    await emitNfce(
      pendingNfceOrderId, 
      pendingNfcePayments.map(p => ({
        // Usar fiscal_code configurado, ou deduzir do tipo/nome
        method: getFiscalPaymentCode(p.paymentMethod),
        amount: Math.round(p.amount * 100)
      })),
      customerData || undefined
    );
    
    setPendingNfceOrderId(null);
    setPendingNfcePayments([]);
  }, [pendingNfceOrderId, pendingNfcePayments, emitNfce]);

  const handleSendItems = async () => {
    if (items.length === 0 && comboItems.length === 0 && pendingCancellations.length === 0) return;
    if (!destination) return;

    setIsSending(true);
    try {
      // Block: counter (balcão) orders must be paid immediately, never sent to kitchen as open consumption
      if (destination.type === "counter" && (items.length > 0 || comboItems.length > 0)) {
        toast.error("Balcão exige recebimento imediato", {
          description: "Use Receber (F10) para finalizar a venda. Balcão não pode ficar em aberto.",
        });
        setIsSending(false);
        return;
      }

      // Block sending new items if period is in partial block
      if (isPartialBlock && (items.length > 0 || comboItems.length > 0)) {
        toast.error("Lançamentos bloqueados", {
          description: "O período precisa ser fechado. Apenas recebimentos são permitidos.",
        });
        setIsSending(false);
        return;
      }

      // 1. First, process pending cancellations
      for (const cancellation of pendingCancellations) {
        await cancelSentItem(cancellation.itemId, cancellation.reason);
      }
      
      // 2. Then, send new items (if any - including combos)
      if (items.length > 0 || comboItems.length > 0) {
        const result = await sendItemsToOrder(
          items,
          orderInfo,
          destination.type === "table" ? destination.id : undefined,
          destination.type === "tab" ? destination.id : undefined,
          orderInfo.peopleCount,
          comboItems
        );

        if (!result) {
          throw new Error("Falha ao enviar itens");
        }

        // If customer was identified before the order existed, attach it now
        if (pendingCustomer && (pendingCustomer.customerId || pendingCustomer.customerCpf)) {
          try {
            await supabase
              .from("orders")
              .update({
                customer_id: pendingCustomer.customerId,
                customer_cpf: pendingCustomer.customerCpf,
              })
              .eq("id", result.id);
          } catch (err) {
            console.error("[PDV] Failed to attach pending customer to new order", err);
          }
          setPendingCustomer(null);
        }
      }

      // 3. Success - clear everything and return to selection
      toast.success("Alterações salvas!", {
        description: `${destination.type === "table" ? "Mesa" : "Comanda"} ${destination.number}`,
      });
      clearCart(); // Clear items AND destination to return to selection screen
      setPendingCancellations([]); // Clear pending cancellations
      if (requireOperatorLogin) setOperatorAuthenticated(false); // Force re-identification
      
    } catch (error: any) {
      console.error("Error processing changes:", error);
      toast.error("Erro ao processar alterações", {
        description: error.message || "Tente novamente.",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handlePrintConference = async () => {
    if (!destination || !activeRestaurant) {
      toast.error("Selecione uma mesa ou comanda primeiro");
      return;
    }

    await printConferenceBill(
      activeRestaurant.id,
      undefined,
      destination.type === "table" ? destination.id : undefined,
      destination.type === "tab" ? destination.id : undefined
    );
  };

  const handleSelectDestination = (dest: SelectedDestination, peopleCount?: number) => {
    selectDestination(dest, peopleCount);
    setSelectedCategory(null);
    setSearchQuery("");
  };

  const handleBackToSelection = () => {
    if (items.length > 0 || comboItems.length > 0) {
      const msg = destination?.type === "counter"
        ? "Venda em andamento. Deseja cancelar e descartar os itens?"
        : "Você tem itens no carrinho. Deseja voltar e perder os itens?";
      const confirm = window.confirm(msg);
      if (!confirm) return;
    }
    selectDestination(null);
    setSelectedCategory(null);
    setSearchQuery("");
  };

  const handleEditItemNotes = (item: { product: { id: string; name: string }; notes?: string }) => {
    setQuickNotesDialog({
      open: true,
      productId: item.product.id,
      productName: item.product.name,
      currentNotes: item.notes || "",
    });
  };

  const isSearching = searchQuery.length > 0;

  // Handler for increasing sent item quantity (add to cart)
  const handleIncreaseItemQuantity = useCallback((item: typeof sentItems[0]) => {
    const product = products?.find(p => p.id === item.product_id);
    if (product) {
      addItem(product, 1, item.notes || undefined);
    } else {
      // If product not found, create a synthetic product
      addItem({
        id: item.product_id || `temp-${item.id}`,
        name: item.product_name,
        price: item.unit_price,
      } as any, 1, item.notes || undefined);
    }
  }, [products, addItem]);

  // Handler for updating cart item quantity
  const handleUpdateCartQuantity = useCallback((productId: string, delta: number) => {
    const item = items.find(i => i.product.id === productId);
    if (item) {
      const newQuantity = item.quantity + delta;
      if (newQuantity <= 0) {
        removeItem(productId);
      } else {
        updateQuantity(productId, newQuantity);
      }
    }
  }, [items, removeItem, updateQuantity]);

  // Determine what content to show based on current state
  let pageContent: React.ReactNode;

  // Show blocking overlays based on period/cash register status
  // Priority: 1. Manual close required, 2. Period closed, 3. Cash register closed
  if (!cashRegisterLoading && isFullBlock && manualCloseStatus) {
    pageContent = (
      <PeriodCloseRequiredOverlay
        onExit={handleExitPDV}
        hasCashRegisterOpen={manualCloseStatus.hasCashRegisterOpen}
        periodDate={manualCloseStatus.periodDate}
        hoursSinceLastActivity={manualCloseStatus.hoursSinceLastActivity}
        hasOpenConsumptions={false}
        openConsumptionsCount={0}
        canTransferPending={manualCloseStatus.canTransferPending}
      />
    );
  } else if (!cashRegisterLoading && !isPeriodOpen) {
    pageContent = <PeriodClosedOverlay onExit={handleExitPDV} />;
  } else if (!cashRegisterLoading && !isCashRegisterOpen) {
    pageContent = <CashRegisterClosedOverlay onExit={handleExitPDV} />;
  } else if (requireOperatorLogin && !operatorAuthenticated && pdvOperators.length > 0) {
    pageContent = (
      <PDVOperatorLogin
        operators={pdvOperators}
        onLogin={(name) => {
          setUserName(name);
          setOperatorAuthenticated(true);
        }}
        restaurantName={activeRestaurant?.name || ""}
      />
    );
  } else if (!destination) {
    // Show selection screen if no destination selected
    pageContent = (
      <div className="flex flex-col h-full">
        {isPartialBlock && manualCloseStatus && (
          <div className="bg-amber-500/90 text-black px-4 py-2 flex items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Ban className="h-4 w-4 shrink-0" />
              <span>
                Novos lançamentos bloqueados — período precisa ser fechado.
                {manualCloseStatus.openConsumptionsCount > 0 && ` Receba os ${manualCloseStatus.openConsumptionsCount} consumo(s) pendente(s) primeiro.`}
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0 bg-black/20 hover:bg-black/30 text-white border-0"
              onClick={() => {
                if (document.fullscreenElement) document.exitFullscreen().catch(console.error);
                navigate("/dashboard/caixa");
              }}
            >
              {manualCloseStatus.hasCashRegisterOpen ? "Fechar Caixa" : "Fechar Período"}
            </Button>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <PDVSelectionScreen
            tables={tables}
            commands={commands}
            onSelectDestination={handleSelectDestination}
            onExit={handleExitPDV}
            onViewOpenBills={() => navigate("/dashboard/consumo-aberto")}
            onViewDeliveryOrders={() => navigate("/dashboard/delivery")}
            onViewMap={() => navigate("/dashboard/mesas")}
            operatorName={userName || undefined}
            restaurantName={activeRestaurant?.name || undefined}
          />
        </div>
      </div>
    );
  } else if (checkoutOpen) {
    // Show checkout screen when checkout is open
    pageContent = (
      <CheckoutScreen
        orderInfo={orderInfo}
        onOrderInfoChange={setOrderInfo}
        subtotal={combinedSubtotal}
        itemCount={combinedItemCount}
        items={items}
        sentItems={sentItems}
        cancelledItems={cancelledItems}
        onConfirm={handleConfirmOrder}
        onBack={() => setCheckoutOpen(false)}
        isSubmitting={isSubmitting}
        destinationLabel={getDestinationLabel()}
        tableId={destination.type === "table" ? destination.id : undefined}
        commandId={destination.type === "tab" ? destination.id : undefined}
        orderId={existingOrderId || undefined}
        orderNumber={existingOrderNumber || undefined}
        onUpdateCartQuantity={handleUpdateCartQuantity}
        onDecreaseItemQuantity={decreaseItemQuantity}
        onIncreaseItemQuantity={handleIncreaseItemQuantity}
        fetchError={orderFetchError}
        onRetryFetch={refetchSentItems}
        onNfceEmission={handleNfceEmission}
      />
    );
  } else {
    // Order mode - show products and cart
    pageContent = (
    <div className="pdv-theme flex flex-col h-full min-h-0 overflow-hidden">
      {/* Cache expiration alert */}
      {activeRestaurant?.id && (
        <CacheExpirationAlert
          restaurantId={activeRestaurant.id}
          isOnline={isOnline}
        />
      )}

      {/* Period blocked banner - partial block mode */}
      {isPartialBlock && manualCloseStatus && (
        <div className="bg-amber-500/90 text-black px-4 py-2 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Ban className="h-4 w-4 shrink-0" />
            <span>
              Novos lançamentos bloqueados — período do dia {manualCloseStatus.periodDate ? format(new Date(manualCloseStatus.periodDate + "T12:00:00"), "dd/MM", { locale: ptBR }) : ""} precisa ser fechado.
              {manualCloseStatus.openConsumptionsCount > 0 && ` ${manualCloseStatus.openConsumptionsCount} consumo(s) pendente(s) de recebimento.`}
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0 bg-black/20 hover:bg-black/30 text-white border-0"
            onClick={() => {
              if (document.fullscreenElement) document.exitFullscreen().catch(console.error);
              navigate("/dashboard/caixa");
            }}
          >
            {manualCloseStatus.hasCashRegisterOpen ? "Fechar Caixa" : "Fechar Período"}
          </Button>
        </div>
      )}

      {/* Header with destination info */}
      <PDVOrderHeader
        destination={destination}
        onBack={handleBackToSelection}
        quantity={itemQuantity}
        onQuantityChange={setItemQuantity}
        tableName={destination.type === "table" ? tables.find(t => t.id === destination.id)?.name : undefined}
        onUpdateTableName={destination.type === "table" ? async (name) => {
          if (destination.id) {
            await updateTable(destination.id, { name });
            await fetchTables();
          }
        } : undefined}
        commandName={destination.type === "tab" ? commands.find(c => c.id === destination.id)?.customer_name : undefined}
        onUpdateCommandName={destination.type === "tab" ? async (name) => {
          if (destination.id) {
            await updateCommand(destination.id, { customer_name: name });
            await fetchCommands();
          }
        } : undefined}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Side - Items List */}
        <div className="w-[220px] shrink-0 border-r border-[var(--pdv-border)] bg-[var(--pdv-surface-panel,#0b1020)] flex flex-col hidden md:flex">
          <div className="p-2 border-b border-[var(--pdv-border)]">
            <h3 className="text-[10px] uppercase tracking-wider text-[var(--pdv-text-label,#3a5a8c)] font-semibold">Pedido</h3>
          </div>

          {isConsumptionUnresolved && (
            <div className="p-2 border-b">
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2">
                <div className="text-sm font-medium text-foreground">
                  Mesa/comanda ocupada, mas o consumo ainda não foi localizado.
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Isso pode acontecer por atraso de sincronização (Tablet/QR). Aguarde alguns segundos ou clique em recarregar.
                </div>
                <div className="mt-2 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void refetchSentItems()}
                  >
                    Recarregar consumo
                  </Button>
                </div>
              </div>
            </div>
          )}

          <PDVItemsList
            items={items}
            comboItems={comboItems}
            sentItems={sentItems}
            cancelledItems={cancelledItems}
            pendingCancellations={pendingCancellations}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onEditNotes={handleEditItemNotes}
            onRemoveCombo={removeCombo}
            onUpdateComboQuantity={updateComboQuantity}
            onMarkForCancellation={markForCancellation}
            onUndoCancellation={undoCancellation}
          />
        </div>

        {/* Right Side - Categories and Products */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Search Bar */}
          <div className="p-2 border-b border-[var(--pdv-border)] bg-[var(--pdv-surface-elevated,#0d1226)] shrink-0">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--pdv-text-label,#3a5a8c)]" />
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar produto ou código de barras..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-12 h-9 bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] text-[var(--pdv-text,#c8dff5)] placeholder:text-[var(--pdv-text-label,#3a5a8c)] rounded-lg text-[13px]"
                  data-barcode-scanner
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {isScanning && (
                    <Barcode className="h-4 w-4 text-[var(--pdv-accent)] animate-pulse" />
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-[var(--pdv-text-muted)] hover:bg-[var(--pdv-highlight-hover)]"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Sair da tela cheia (F11)" : "Tela cheia (F11)"}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </Button>
              <OfflineStatusIndicator
                isOnline={isOnline}
                isSyncing={isSyncing}
                pendingCount={pendingCount}
                onSync={triggerSync}
              />
              <SQLiteStatusIndicator />
              <WaiterSessionsDialog />
            </div>
          </div>

          {/* Category Tabs - Scrollable horizontal list */}
          {activeCategories.length > 0 && !isSearching && (
            <div className="border-b border-[var(--pdv-border)] bg-[var(--pdv-bg)] shrink-0">
              <div className="px-2 py-2">
                <CategoryTabs
                  categories={activeCategories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1 overflow-auto p-3 min-h-0 pdv-scrollable bg-[var(--pdv-bg)]">
            {productsLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-[var(--pdv-text-subtle)]">Carregando produtos...</p>
              </div>
            ) : activeProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingCart className="h-12 w-12 text-[var(--pdv-text-subtle)] opacity-30 mb-3" />
                <h2 className="text-base font-medium mb-1 text-[var(--pdv-text)]">Nenhum produto ativo</h2>
                <p className="text-sm text-[var(--pdv-text-subtle)] max-w-md">
                  Cadastre e ative produtos para começar a vender.
                </p>
              </div>
            ) : isSearching ? (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  {filteredProducts.length} produto(s) encontrado(s)
                </p>
                <ProductGrid
                  products={filteredProducts}
                  combos={combos}
                  onProductClick={handleProductClick}
                  onComboClick={handleComboClick}
                  stockAvailability={productStockMap}
                  blockSalesWithoutStock={stockBlockSales}
                  categoryColor={selectedCategoryColor}
                />
              </>
            ) : (
              <ProductGrid
                products={filteredProducts}
                combos={combos}
                onProductClick={handleProductClick}
                onComboClick={handleComboClick}
                stockAvailability={productStockMap}
                blockSalesWithoutStock={stockBlockSales}
                categoryColor={selectedCategoryColor}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <PDVActionBar
        subtotal={combinedSubtotal}
        serviceTax={serviceTaxAmount}
        serviceTaxPercentage={serviceTaxPercentage}
        entryAmount={entryAmount}
        entryTypeName={entryTypeName}
        total={displayTotal}
        itemCount={combinedItemCount}
        newItemCount={itemCount}
        pendingCancellationsCount={pendingCancellations.length}
        onBack={handleBackToSelection}
        onCheckout={handleCheckout}
        onSendItems={handleSendItems}
        isSending={isSending}
        onSwitchToDelivery={() => toast.info("Delivery em desenvolvimento")}
        onCustomerData={() => {
          setCustomerDataOpen(true);
        }}
        onSetNickname={() => setNicknameOpen(true)}
        currentNickname={orderInfo.customerName || null}
        showNicknameButton={
          destination?.type === "table" ||
          destination?.type === "tab" ||
          destination?.type === "counter"
        }
        onGroupTickets={() => {
          if (!existingOrderId) {
            toast.error("Envie os itens primeiro antes de agrupar");
            return;
          }
          setGroupTicketsOpen(true);
        }}
        onSplitBill={() => toast.info("Dividir conta em desenvolvimento")}
        onTransferItems={() => {
          if (!existingOrderId) {
            toast.error("Envie os itens primeiro antes de transferir");
            return;
          }
          if (sentItems.length === 0) {
            toast.error("Nenhum item enviado para transferir");
            return;
          }
          setTransferItemsOpen(true);
        }}
        onCancelOrder={() => {
          if (!existingOrderId) {
            toast.error("Nenhum pedido ativo para cancelar");
            return;
          }
          if (sentItems.length === 0) {
            toast.error("Nenhum item enviado para cancelar");
            return;
          }
          setCancelOrderOpen(true);
        }}
        onViewDetails={() => {
          if (!existingOrderId) {
            toast.error("Nenhum pedido ativo para ver detalhes");
            return;
          }
          setOrderDetailsOpen(true);
        }}
        onPrint={handlePrintConference}
        onConsultSales={() => navigate("/dashboard/pedidos")}
        showKeyboardShortcuts={Boolean(activeRestaurant?.show_pdv_keyboard_shortcuts)}
        hasExistingOrder={Boolean(existingOrderId)}
        hideSendButton={destination?.type === "counter"}
      />
    </div>
    );
  }

  // Final return: render pageContent + globally mounted modals
  return (
    <>
      {pageContent}

      {/* Quick Notes Dialog */}
      <ItemNotesDialog
        open={quickNotesDialog.open}
        onOpenChange={(open) =>
          setQuickNotesDialog((prev) => ({ ...prev, open }))
        }
        productName={quickNotesDialog.productName}
        currentNotes={quickNotesDialog.currentNotes}
        onSaveNotes={handleQuickNotesSave}
      />

      {/* Combo Detail Sheet */}
      <ComboDetailSheet
        key={selectedCombo?.id ?? 'combo-empty'}
        combo={selectedCombo}
        open={comboSheetOpen}
        onOpenChange={setComboSheetOpen}
        onAddToCart={handleAddComboToCart}
      />

      {/* NFC-e Modals - at page level so they persist when checkout closes */}
      <CustomerIdentificationDialog
        open={customerIdDialogOpen}
        onOpenChange={setCustomerIdDialogOpen}
        onConfirm={handleCustomerIdentificationConfirm}
        isLoading={isEmittingNfce}
      />

      <NfceEmissionDialog
        open={nfceDialogOpen}
        onOpenChange={setNfceDialogOpen}
        isEmitting={isEmittingNfce}
        result={nfceResult}
      />

      <GroupTicketsDialog
        open={groupTicketsOpen}
        onOpenChange={setGroupTicketsOpen}
        currentType={destination?.type === "tab" ? "command" : "table"}
        currentNumber={destination?.number || ""}
        currentOrderId={existingOrderId}
        onSuccess={() => {
          // After grouping, force return to selection screen so user sees updated map
          clearCart();
          selectDestination(null);
          fetchTables();
          fetchCommands();
        }}
      />

      <TransferItemsDialog
        open={transferItemsOpen}
        onOpenChange={setTransferItemsOpen}
        sentItems={sentItems}
        currentOrderId={existingOrderId}
        currentType={destination?.type === "tab" ? "command" : "table"}
        currentNumber={destination?.number || ""}
        onSuccess={() => void refetchSentItems()}
      />

      <CancelOrderDialog
        open={cancelOrderOpen}
        onOpenChange={setCancelOrderOpen}
        sentItems={sentItems}
        orderId={existingOrderId}
        destination={destination}
        cancelSentItem={cancelSentItem}
        onSuccess={() => {
          handleBackToSelection();
        }}
      />

      <CustomerDataDialog
        open={customerDataOpen}
        onOpenChange={setCustomerDataOpen}
        orderId={existingOrderId}
        restaurantId={activeRestaurant?.id}
        onIdentifiedWithoutOrder={(data) => {
          setPendingCustomer({
            customerId: data.customerId,
            customerCpf: data.customerCpf,
            customerName: data.name,
          });
          if (data.name) {
            setOrderInfo((prev) => ({ ...prev, customerName: data.name || prev.customerName }));
          }
        }}
        onClearedWithoutOrder={() => {
          setPendingCustomer(null);
        }}
      />

      <OrderDetailsPanel
        open={orderDetailsOpen}
        onOpenChange={setOrderDetailsOpen}
        orderId={existingOrderId}
      />

      <NicknameDialog
        open={nicknameOpen}
        onOpenChange={setNicknameOpen}
        initialValue={orderInfo.customerName ?? ""}
        onSave={handleSetNickname}
      />
    </>
  );
}
